import React, { useEffect } from 'react';
import { Sprout, Heart, Sun, GraduationCap, Home as HomeIcon, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { siteContent } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InsuranceWidget from '../components/InsuranceWidget';
import TestimonialCarousel from '../components/TestimonialCarousel';
import '../styles/insurance-widget.css';
import '../styles/testimonial-carousel.css';
import '../styles/inspire.css';
import '../styles/inspire-home.css';

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    preferredContact: 'Email',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);

  const iconMap = {
    'Sprout': Sprout,
    'Heart': Heart,
    'Sun': Sun,
    'GraduationCap': GraduationCap,
    'Home': HomeIcon
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Call backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, source: 'web' })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      console.log('Contact form submitted successfully:', data);
      
      setIsSubmitted(true);
      setShowModal(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferredContact: 'Email',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('There was an error submitting your form. Please try again.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsSubmitted(false);
  };

  // Handle scroll to section when navigating from other pages
  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  return (
    <div className="therapy-site">
      <Header />

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content container">
          <div className="hero-text fade-in-up">
            <h1 className="heading-hero font-serif gradient-text">{siteContent.hero.title}</h1>
            <p className="hero-tagline body-large">{siteContent.hero.tagline}</p>
            <p className="hero-services body-medium">{siteContent.hero.subtitle}</p>
            <p className="hero-subtitle body-medium">{siteContent.hero.body}</p>
            <div className="hero-cta-group">
              <button 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="btn btn-primary"
                data-testid="hero-cta-primary"
              >
                {siteContent.hero.ctaPrimary}
              </button>
              <button 
                onClick={() => document.getElementById('insurance').scrollIntoView({ behavior: 'smooth' })}
                className="btn btn-secondary-outline"
                data-testid="hero-cta-secondary"
              >
                {siteContent.hero.ctaSecondary}
              </button>
            </div>
          </div>
          
          {/* Rising Sun decoration */}
          <svg className="hero-sun-icon" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="sunGradientHero" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F9C8A8" />
                <stop offset="50%" stopColor="#E6A9C0" />
                <stop offset="100%" stopColor="#C4A8E2" />
              </linearGradient>
            </defs>
            <line x1="10" y1="45" x2="70" y2="45" stroke="url(#sunGradientHero)" strokeWidth="1.5" opacity="0.35" />
            <path d="M 40 45 A 25 25 0 0 1 15 45" stroke="url(#sunGradientHero)" strokeWidth="8" fill="none" opacity="0.1" />
            <path d="M 40 45 A 20 20 0 0 1 20 45" stroke="url(#sunGradientHero)" strokeWidth="6" fill="none" opacity="0.25" />
            <path d="M 40 45 A 15 15 0 0 1 25 45 L 40 45 Z" fill="url(#sunGradientHero)" opacity="0.75" />
          </svg>
        </div>
      </section>

      {/* Start Here Strip */}
      <section className="start-here-strip" data-testid="start-here-strip">
        <div className="container">
          <div className="start-here-steps">
            <div className="start-step fade-in-up">
              <span className="step-number">1</span>
              <p className="step-text">Tell us who needs support</p>
            </div>
            <div className="step-connector" />
            <div className="start-step fade-in-up">
              <span className="step-number">2</span>
              <p className="step-text">We reply within 1 business day</p>
            </div>
            <div className="step-connector" />
            <div className="start-step fade-in-up">
              <span className="step-number">3</span>
              <p className="step-text">Book via insurance or consult</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Adrian Section */}
      <section id="about" className="about-section section">
        <div className="container">
          <div className="about-grid">
            <div className="about-image fade-in">
              <div className="therapy-office-image">
                <img 
                  src="https://customer-assets.emergentagent.com/job_50e26051-612f-4752-a7df-90e8e02588f2/artifacts/tz68laxf_IMG_6580.JPG" 
                  className="adrian-photo"
                />
              </div>
            </div>
            
            <div className="about-content fade-in-up">
              <h2 className="heading-2 font-serif gradient-text">{siteContent.about.heading}</h2>
              {siteContent.about.bio.map((paragraph, index) => (
                <p key={index} className="body-medium about-paragraph">{paragraph}</p>
              ))}
              <div className="credentials">
                <p className="credentials-name heading-3 font-serif">{siteContent.about.credentials}</p>
                <p className="credentials-full body-small">{siteContent.about.credentialsFull}</p>
                <p className="location body-small">{siteContent.about.location}</p>
                <div className="violet-badge-row">
                  <a href="/step-up-scholarship" className="violet-badge-link" data-testid="step-up-badge-about">
                    <img 
                      src="https://customer-assets-7cd3h4nn.emergentagent.net/job_50e26051-612f-4752-a7df-90e8e02588f2/artifacts/pxsfg4tg_IMG_9986.jpeg" 
                      alt="Step Up For Students logo" 
                      className="violet-badge-img"
                      style={{ background: 'white', padding: '4px', borderRadius: '4px' }}
                    />
                  </a>
                  <p className="body-small violet-badge-text">Inspire Therapy Collective is an approved Step Up For Students EMA Marketplace provider.</p>
                </div>
                <div className="violet-badge-row">
                  <a href="https://www.joinviolet.com" target="_blank" rel="noopener noreferrer" className="violet-badge-link" data-testid="violet-badge-about">
                    <img 
                      src="https://app.joinviolet.com/api/v1/users/ZjcxNzc3Y2QtMWJhMy00ZDE3LWIzNjQtN2I3N2U5ODRiNjRk/benchmark_image?communities=neurodivergence" 
                      alt="Violet Verified — Neurodivergence Cultural Awareness" 
                      className="violet-badge-img"
                    />
                  </a>
                  <p className="body-small violet-badge-text">I hold a Violet Verified Neurodivergence benchmark — because your neurodivergent child deserves a therapist who truly understands how they experience the world.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section section texture-overlay">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">{siteContent.services.heading}</h2>
          
          <div className="services-grid">
            {siteContent.services.items.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div key={index} className="service-card card fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="service-icon-wrapper">
                    <IconComponent className="service-icon" size={32} />
                  </div>
                  <h3 className="heading-3 font-serif">{service.title}</h3>
                  <p className="body-medium">{service.description}</p>
                  <button 
                    onClick={() => navigate(service.link)}
                    className="btn btn-secondary btn-service"
                    data-testid={`learn-more-${service.title.toLowerCase()}`}
                  >
                    Learn More
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Step Up Scholarship Banner */}
      <section className="step-up-banner section-sm" data-testid="step-up-homepage-banner">
        <div className="container">
          <div className="step-up-banner-content fade-in-up">
            <h2 className="heading-2 font-serif gradient-text">Use Your Step Up Scholarship With Us</h2>
            <p className="body-medium">
              Is your child enrolled in a Step Up For Students scholarship? We're an approved provider on the EMA Marketplace and bill your child's scholarship directly for counseling, so there's no out-of-pocket cost when funds are available.
            </p>
            <button 
              onClick={() => navigate('/step-up-scholarship')}
              className="btn btn-primary"
              data-testid="step-up-homepage-cta"
            >
              Learn How It Works
            </button>
          </div>
        </div>
      </section>

      {/* My Approach Section */}
      <section className="approach-section section">
        <div className="container approach-container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">{siteContent.approach.heading}</h2>
          
          <svg className="sun-divider fade-in" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="sunDivider" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F9C8A8" />
                <stop offset="50%" stopColor="#E6A9C0" />
                <stop offset="100%" stopColor="#C4A8E2" />
              </linearGradient>
            </defs>
            <line x1="10" y1="45" x2="70" y2="45" stroke="url(#sunDivider)" strokeWidth="1.5" opacity="0.35" />
            <path d="M 40 45 A 25 25 0 0 1 15 45" stroke="url(#sunDivider)" strokeWidth="8" fill="none" opacity="0.1" />
            <path d="M 40 45 A 20 20 0 0 1 20 45" stroke="url(#sunDivider)" strokeWidth="6" fill="none" opacity="0.25" />
            <path d="M 40 45 A 15 15 0 0 1 25 45 L 40 45 Z" fill="url(#sunDivider)" opacity="0.75" />
          </svg>
          
          <div className="approach-content fade-in-up">
            {siteContent.approach.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="body-large">{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Good Fit Section */}
      <section className="goodfit-section section-sm texture-overlay">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">{siteContent.goodFit.heading}</h2>
          
          <div className="goodfit-list fade-in-up">
            {siteContent.goodFit.items.map((item, index) => (
              <div key={index} className="goodfit-item">
                <svg className="bullet-sun" viewBox="0 0 40 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id={`sunBullet${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#F9C8A8" />
                      <stop offset="50%" stopColor="#E6A9C0" />
                      <stop offset="100%" stopColor="#C4A8E2" />
                    </linearGradient>
                  </defs>
                  <line x1="5" y1="22" x2="35" y2="22" stroke={`url(#sunBullet${index})`} strokeWidth="1" opacity="0.35" />
                  <path d="M 20 22 A 12 12 0 0 1 8 22" stroke={`url(#sunBullet${index})`} strokeWidth="4" fill="none" opacity="0.1" />
                  <path d="M 20 22 A 10 10 0 0 1 10 22" stroke={`url(#sunBullet${index})`} strokeWidth="3" fill="none" opacity="0.25" />
                  <path d="M 20 22 A 7 7 0 0 1 13 22 L 20 22 Z" fill={`url(#sunBullet${index})`} opacity="0.75" />
                </svg>
                <p className="body-medium">{item}</p>
              </div>
            ))}
          </div>
          
          <p className="goodfit-closing body-large text-center fade-in-up">{siteContent.goodFit.closingLine}</p>
        </div>
      </section>

      {/* What the First Session Looks Like */}
      <section className="first-session-section section-sm" data-testid="first-session-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">What the First Session Looks Like</h2>
          <div className="first-session-content fade-in-up">
            <p className="body-large">Your first session is a chance to get to know each other. We'll talk about what brought you here, what you're hoping for, and how I can best support you or your child. There's no pressure — just a warm, honest conversation to see if we're a good fit.</p>
            <div className="first-session-details">
              <div className="session-detail-item">
                <span className="session-detail-label">Session Length</span>
                <span className="session-detail-value">50 minutes (individual) · 55 minutes (couples)</span>
              </div>
              <div className="session-detail-item">
                <span className="session-detail-label">Location</span>
                <span className="session-detail-value">In-person in Lake Nona, Orlando FL · Telehealth across FL & VA</span>
              </div>
              <div className="session-detail-item">
                <span className="session-detail-label">Kids & Teens</span>
                <span className="session-detail-value">A parent may join part of the first session</span>
              </div>
              <div className="session-detail-item">
                <span className="session-detail-label">Phone</span>
                <span className="session-detail-value"><a href="tel:4072019895">(407) 201-9895</a></span>
              </div>
              <div className="session-detail-item">
                <span className="session-detail-label">Email</span>
                <span className="session-detail-value"><a href="mailto:amirabile@inspiretherapycollective.com">amirabile@inspiretherapycollective.com</a></span>
              </div>
              <div className="session-detail-item">
                <span className="session-detail-label">Office Address</span>
                <span className="session-detail-value">Lake Nona area, Orlando FL — exact address provided upon booking</span>
              </div>
              <div className="session-detail-item">
                <span className="session-detail-label">Hours</span>
                <span className="session-detail-value">By appointment — reach out to check availability</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Placeholder */}
      <section className="testimonials-placeholder section-sm" data-testid="testimonials-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">What Families Are Saying</h2>
          <div className="testimonials-grid fade-in-up">
            <div className="testimonial-placeholder-card">
              <p className="placeholder-quote-text">Client testimonial coming soon.</p>
            </div>
            <div className="testimonial-placeholder-card">
              <p className="placeholder-quote-text">Client testimonial coming soon.</p>
            </div>
            <div className="testimonial-placeholder-card">
              <p className="placeholder-quote-text">Client testimonial coming soon.</p>
            </div>
          </div>
          <p className="testimonials-coming-note fade-in-up">Google & Psychology Today reviews coming soon.</p>
        </div>
      </section>

      {/* In Their Own Words */}
      <TestimonialCarousel />

      {/* Contact Section */}
      <section id="contact" className="contact-section section texture-overlay">
        <div className="container">
          {/* Insurance Section */}
          <div className="insurance-section fade-in-up" id="insurance">
            <div className="insurance-card card">
              <h3 className="heading-3 font-serif text-center gradient-text">Insurance & Rates</h3>
              <p className="body-medium insurance-text">
                I currently accept the following insurance plans. Select yours to find out how to book a session.
              </p>
              <InsuranceWidget />
              <div className="insurance-cashpay fade-in-up">
                <p className="insurance-cashpay-text">
                  Paying out of pocket? Individual $150 / Couples $175 — sliding scale available. <a href="/fees-documentation" className="insurance-cashpay-link">See all fees</a>
                </p>
              </div>
              <div className="insurance-consult-cta fade-in-up">
                <button 
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="btn-insurance-consult"
                  data-testid="insurance-consult-cta"
                >
                  Book a Free 15-Min Consult
                </button>
              </div>
            </div>
          </div>

          <div className="contact-header text-center fade-in-up">
            <h2 className="heading-2 font-serif gradient-text">{siteContent.contact.heading}</h2>
            <p className="body-large">{siteContent.contact.subtitle}</p>
          </div>

          <div className="contact-grid">
            <div className="contact-form-wrapper fade-in-up">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label body-small">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Your name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label body-small">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label body-small">Phone (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="(123) 456-7890"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="preferredContact" className="form-label body-small">Preferred Contact Method</label>
                  <select
                    id="preferredContact"
                    name="preferredContact"
                    value={formData.preferredContact}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="Email">Email</option>
                    <option value="Phone">Phone</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label body-small">
                    How can I help? (Please avoid sharing sensitive health details here — we'll discuss everything privately during your consultation.) *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="form-textarea"
                    rows="5"
                    placeholder="Tell me a bit about what you're looking for..."
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-submit" disabled={isSubmitted}>
                  {isSubmitted ? '✓ Message Sent!' : 'Send Message'}
                </button>
                
                <p className="form-privacy-note caption">
                  This form is for general inquiries only. Please do not include protected health information. Clinical details will be discussed during your private consultation.
                </p>
              </form>
            </div>

            <div className="contact-info fade-in-up">
              <div className="info-card card">
                <div className="info-item">
                  <svg className="info-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div>
                    <p className="body-medium">{siteContent.contact.location}</p>
                    <p className="caption">{siteContent.contact.telehealth}</p>
                  </div>
                </div>

                <div className="info-item">
                  <svg className="info-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1469 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.095 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65162C2.82196 2.44655 3.0498 2.28271 3.30379 2.17052C3.55777 2.05833 3.83233 2.00026 4.10999 2H7.10999C7.5953 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04207 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97366 7.27691 9.8939 7.65088C9.81415 8.02485 9.62886 8.36811 9.35999 8.64L8.08999 9.91C9.51355 12.4135 11.5864 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9751 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <a href={`tel:${siteContent.contact.phone}`} className="body-medium contact-link">
                    {siteContent.contact.phone}
                  </a>
                </div>

                <div className="info-item">
                  <svg className="info-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <a href={`mailto:${siteContent.contact.email}`} className="body-medium contact-link">
                    {siteContent.contact.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Thank You Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal} aria-label="Close">
              <X size={24} />
            </button>
            
            <div className="modal-body">
              <h2 className="modal-title">
              Thank you for reaching out!{' '}
              <svg className="modal-heart" width="28" height="26" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="heartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#F9C8A8" />
                    <stop offset="50%" stopColor="#E6A9C0" />
                    <stop offset="100%" stopColor="#C4A8E2" />
                  </linearGradient>
                </defs>
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="url(#heartGradient)"/>
              </svg>
            </h2>
              <p className="modal-text">
                Adrian has received your message and will be in touch within 1 business day.
              </p>
              
              <div className="modal-crisis">
                <p className="modal-crisis-intro">
                  If you or someone you know is experiencing a crisis or needs immediate support:
                </p>
                <ul className="modal-crisis-list">
                  <li>
                    <strong>988 Suicide & Crisis Lifeline</strong> — Call or text <a href="tel:988">988</a> (available 24/7)
                  </li>
                  <li>
                    <strong>Crisis Text Line</strong> — Text HOME to <a href="sms:741741">741741</a>
                  </li>
                  <li>
                    <strong>Emergency</strong> — Call <a href="tel:911">911</a>
                  </li>
                </ul>
                <p className="modal-reassurance">You are not alone.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
